import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useData } from '../../contexts/DataContext';
import { isCompletedStatus } from '../../utils/assignments';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, parseISO, startOfDay, startOfWeek, endOfWeek, isToday, startOfWeek as startOfWeekFn, endOfWeek as endOfWeekFn, addWeeks, subWeeks, startOfDay as startOfDayFn, isTomorrow, addDays, subDays } from 'date-fns';
import CalendarUpgradePrompt from './CalendarUpgradePrompt';
import CalendarAssignmentEditModal from './CalendarAssignmentEditModal';
import SubscriptionModal from '../Layout/SubscriptionModal';
import { FaStickyNote } from 'react-icons/fa';

// View constants
const VIEW_TYPES = {
  MONTH: 'month',
  WEEK: 'week',
  DAY: 'day',
  AGENDA: 'agenda'
};

const CalendarContainer = styled.div`
  height: 100vh;
  width: 100%;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;

  @media (max-width: 768px) {
    height: 100vh;
    width: 100%;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${props => props.theme.backgroundSecondary};
  padding: 0.75rem;
  z-index: 10;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 768px) {
    padding: 0.5rem;
  }

  @media (max-width: 480px) {
    gap: 0.5rem;
    padding: 0.75rem;
  }
`;

const TodayButton = styled.button`
  padding: 0.5rem 1rem;
  background: ${props => props.theme.buttonPrimary};
  border: 1px solid ${props => props.theme.buttonPrimary};
  border-radius: 4px;
  color: ${props => props.theme.text};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;

  &:hover {
    background: ${props => props.theme.buttonPrimaryHover};
    border-color: ${props => props.theme.buttonPrimaryHover};
  }

  @media (max-width: 480px) {
    padding: 0.375rem 0.75rem;
    font-size: 0.75rem;
  }
`;

const NavigationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ArrowButton = styled.button`
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  width: 32px;
  height: 32px;
  color: ${props => props.theme.text};
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.theme.cardBackground};
  }

  @media (max-width: 480px) {
    width: 28px;
    height: 28px;
    font-size: 0.875rem;
  }
`;

const DateDisplay = styled.button`
  background: transparent;
  border: none;
  color: ${props => props.theme.text};
  font-size: 1.25rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: ${props => props.theme.cardBackground};
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    padding: 0.375rem 0.75rem;
  }
`;

const ViewDropdown = styled.select`
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  color: ${props => props.theme.text};
  font-weight: 600;
  cursor: pointer;
  padding: 0.5rem 1rem;
  transition: all 0.2s ease;
  font-size: 0.875rem;

  &:hover {
    background: ${props => props.theme.borderLight};
    border-color: ${props => props.theme.borderLight};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
    box-shadow: 0 0 0 2px rgba(88, 101, 242, 0.2);
  }

  @media (max-width: 480px) {
    padding: 0.375rem 0.75rem;
    font-size: 0.75rem;
  }
`;

const AdditionalControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-left: auto;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 0.5rem;
  }
`;

const ZoomControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  padding: 0.25rem;
`;

const ControlButton = styled.button`
  background: ${props => props.disabled ? props.theme.background : props.theme.buttonPrimary};
  border: none;
  border-radius: 4px;
  color: ${props => props.theme.text};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  transition: all 0.2s ease;
  opacity: ${props => props.disabled ? 0.5 : 1};

  &:hover:not(:disabled) {
    background: ${props => props.theme.buttonPrimaryHover};
    transform: scale(1.05);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }
`;

const ZoomIcon = styled.span`
  font-size: 0.8rem;
`;

const ZoomLevel = styled.span`
  color: ${props => props.theme.text};
  font-size: 0.75rem;
  font-weight: 600;
  min-width: 40px;
  text-align: center;
`;

const CompactToggle = styled.div`
  display: flex;
  align-items: center;
`;

const CompactButton = styled.button`
  background: ${props => props.$active ? props.theme.buttonPrimary : props.theme.background};
  border: 1px solid ${props => props.$active ? props.theme.buttonPrimary : props.theme.border};
  border-radius: 6px;
  color: ${props => props.theme.text};
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    background: ${props => props.$active ? props.theme.buttonPrimaryHover : props.theme.cardBackground};
    border-color: ${props => props.$active ? props.theme.buttonPrimaryHover : props.theme.buttonPrimary};
  }

  &:active {
    transform: scale(0.95);
  }
`;

const FilterDropdown = styled.select`
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  color: ${props => props.theme.text};
  font-weight: 600;
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  transition: all 0.2s ease;
  font-size: 0.75rem;

  &:hover {
    background: ${props => props.theme.cardBackground};
    border-color: ${props => props.theme.buttonPrimary};
  }

  &:focus {
    outline: none;
    border-color: ${props => props.theme.buttonPrimary};
    box-shadow: 0 0 0 2px rgba(88, 101, 242, 0.2);
  }

  @media (max-width: 480px) {
    padding: 0.375rem 0.5rem;
    font-size: 0.7rem;
  }
`;

const SidebarToggle = styled.div`
  display: flex;
  align-items: center;
`;

const SidebarButton = styled.button`
  background: ${props => props.$active ? props.theme.buttonPrimary : props.theme.background};
  border: 1px solid ${props => props.$active ? props.theme.buttonPrimary : props.theme.border};
  border-radius: 6px;
  color: ${props => props.theme.text};
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    background: ${props => props.$active ? props.theme.buttonPrimaryHover : props.theme.cardBackground};
    border-color: ${props => props.$active ? props.theme.buttonPrimaryHover : props.theme.buttonPrimary};
  }

  &:active {
    transform: scale(0.95);
  }
`;

const DatePickerDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  padding: 1rem;
  z-index: 2000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  min-width: 280px;
`;

const YearControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const YearDisplay = styled.span`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${props => props.theme.text};
  min-width: 80px;
  text-align: center;
`;

const MonthGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
`;

const MonthButton = styled.button`
  padding: 0.75rem;
  background: ${props => props.selected ? props.theme.buttonPrimary : props.theme.background};
  border: 1px solid ${props => props.selected ? props.theme.buttonPrimary : props.theme.border};
  border-radius: 6px;
  color: ${props => props.theme.text};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.selected ? props.theme.buttonPrimaryHover : props.theme.borderLight};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.16);
  }

  @media (max-width: 480px) {
    padding: 0.5rem;
    font-size: 0.875rem;
  }
`;

const CalendarGridContainer = styled.div`
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 0.5rem;

  @media (max-width: 768px) {
    padding: 0.25rem;
  }

  @media (max-width: 480px) {
    padding: 0.125rem 0.125rem 2rem 0.125rem;
  }
`;

const DayHeadersContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0;
  background: transparent;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 5;
  height: 3rem;

  @media (max-width: 768px) {
    min-width: 600px;
  }
`;

const DaysGridContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.background};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.border};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.textMuted};
  }
  scrollbar-width: thin;
  scrollbar-color: ${props => props.theme.border} ${props => props.theme.background};

  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const DaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-template-rows: repeat(6, 1fr);
  gap: 0;
  height: 100%;
  background: ${props => props.theme.borderLight};
  padding-top: 3rem;

  @media (max-width: 768px) {
    min-width: 600px;
  }
`;

const Day = styled.div`
  background: ${props => props.theme.background};
  padding: 0.5rem;
  min-height: 0;
  position: relative;
  opacity: ${props => props.$isCurrentMonth ? 1 : 0.5};
  border: ${props => props.$isToday ? `1px solid ${props.theme.buttonPrimary}` : `1px solid ${props.theme.border}`};
  box-shadow: ${props => props.$isToday ? '0 0 8px rgba(88, 101, 242, 0.3)' : 'none'};
  border-radius: 0;
  overflow: hidden;
  box-sizing: border-box;

  @media (max-width: 768px) {
    min-width: 100px;
    padding: 0.35rem;
  }

  @media (max-width: 480px) {
    min-width: 80px;
    padding: 0.25rem;
  }
`;

const DayHeader = styled.div`
  background: ${props => props.theme.backgroundSecondary};
  padding: 0.5rem;
  text-align: center;
  font-weight: bold;
  color: ${props => props.theme.text};
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${props => props.theme.border};
`;

const AssignmentIndicator = styled.div`
  position: absolute;
  top: 5px;
  right: 5px;
  width: 8px;
  height: 8px;
  background: ${props => props.color || '#5865f2'};
  border-radius: 50%;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.5);
`;

const AssignmentTitle = styled.div`
  font-size: 0.625rem;
  color: ${props => props.theme.text};
  margin-top: 0.125rem;
  padding: 0.125rem 0.25rem;
  border-radius: 2px;
  margin-bottom: 0.125rem;
  background: ${props => props.color || props.theme.buttonPrimary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: default;

  @media (max-width: 768px) {
    font-size: 0.6rem;
    padding: 0.1rem 0.2rem;
    margin-top: 0.1rem;
  }
`;

const AssignmentNavigateIcon = styled.button`
  position: absolute;
  right: 0.25rem;
  top: 50%;
  transform: translateY(-50%);
  background: ${props => props.theme.buttonPrimary};
  border: 2px solid ${props => props.theme.buttonPrimary};
  border-radius: 4px;
  color: white;
  cursor: pointer;
  padding: 0.25rem;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.9;
  transition: all 0.2s ease;
  min-width: 24px;
  min-height: 24px;

  &:hover {
    opacity: 1;
    background: ${props => props.theme.buttonPrimaryHover};
    border-color: ${props => props.theme.buttonPrimaryHover};
    transform: translateY(-50%) scale(1.05);
  }

  &:focus {
    outline: 2px solid ${props => props.theme.buttonPrimary};
    outline-offset: 2px;
    opacity: 1;
  }

  @media (max-width: 768px) {
    font-size: 0.6rem;
    padding: 0.2rem;
    min-width: 20px;
    min-height: 20px;
  }
`;

const WeekViewContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 200px);
  transform: scale(${props => props.$zoomLevel});
  transform-origin: top left;
  width: ${props => `${100 / props.$zoomLevel}%`};
  height: ${props => `${100 / props.$zoomLevel}%`};
`;

const WeekHeadersContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0;
  background: transparent;
  position: absolute;
  top: 0;
  left: 100px;
  right: 0;
  z-index: 5;
  height: 4rem;

  @media (max-width: 768px) {
    min-width: 600px;
    left: 100px;
  }
`;

const WeekContentContainer = styled.div`
  height: calc(100vh - 250px);
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.background};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.border};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.textMuted};
  }
  scrollbar-width: thin;
  scrollbar-color: ${props => props.theme.border} ${props => props.theme.background};

  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const WeekContentGrid = styled.div`
  display: grid;
  grid-template-columns: 100px repeat(7, 1fr);
  gap: 0;
  background: ${props => props.theme.borderLight};
  height: ${props => props.$isCompactMode ? '960px' : '1440px'};
  padding-top: 4rem;

  @media (max-width: 768px) {
    min-width: 700px;
  }
`;

const TimeColumn = styled.div`
  background: ${props => props.theme.backgroundSecondary};
  display: flex;
  flex-direction: column;
  position: sticky;
  left: 0;
  z-index: 10;
  border-right: 1px solid ${props => props.theme.border};
`;

const TimeSlot = styled.div`
  height: ${props => props.$isCompactMode ? '40px' : '60px'};
  border-bottom: 1px solid ${props => props.theme.cardBackground};
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 0.25rem;
  font-size: 0.75rem;
  color: ${props => props.theme.textSecondary};
  font-weight: 500;

  @media (max-width: 768px) {
    height: ${props => props.$isCompactMode ? '35px' : '50px'};
    font-size: 0.7rem;
  }
`;

const WeekDayColumn = styled.div`
  background: ${props => props.theme.background};
  display: flex;
  flex-direction: column;
  border-right: 1px solid ${props => props.theme.border};
`;

const WeekDayHeader = styled.div`
  background: ${props => props.theme.backgroundSecondary};
  padding: 0.75rem;
  text-align: center;
  font-weight: bold;
  color: ${props => props.theme.text};
  border-bottom: 1px solid ${props => props.theme.border};
  height: 4rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 0.5rem;
    font-size: 0.875rem;
  }
`;

const WeekDaySlot = styled.div`
  height: ${props => props.$isCompactMode ? '40px' : '60px'};
  position: relative;
  background: ${props => props.$isToday ? 'rgba(88, 101, 242, 0.1)' : 'transparent'};

  @media (max-width: 768px) {
    height: ${props => props.$isCompactMode ? '35px' : '50px'};
  }
`;

const WeekAssignmentItem = styled.div`
  position: absolute;
  left: 1px;
  right: 1px;
  background: ${props => props.color || props.theme.buttonPrimary};
  color: ${props => props.theme.text};
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  z-index: 10;
  border: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    font-size: 0.7rem;
    padding: 0.2rem 0.4rem;
  }
`;

const WeekAssignmentNavigateIcon = styled.button`
  position: absolute;
  right: 0.25rem;
  top: 50%;
  transform: translateY(-50%);
  background: ${props => props.theme.buttonPrimary};
  border: 2px solid ${props => props.theme.buttonPrimary};
  border-radius: 4px;
  color: white;
  cursor: pointer;
  padding: 0.25rem;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.9;
  transition: all 0.2s ease;
  min-width: 24px;
  min-height: 24px;

  &:hover {
    opacity: 1;
    background: ${props => props.theme.buttonPrimaryHover};
    border-color: ${props => props.theme.buttonPrimaryHover};
    transform: translateY(-50%) scale(1.05);
  }

  &:focus {
    outline: 2px solid ${props => props.theme.buttonPrimary};
    outline-offset: 2px;
    opacity: 1;
  }

  @media (max-width: 768px) {
    font-size: 0.6rem;
    padding: 0.2rem;
    min-width: 20px;
    min-height: 20px;
  }
`;

const DayView = ({ currentDate, assignments, courses, settings, onAssignmentHover, onAssignmentLeave, onAssignmentClick, onAssignmentNavigate, zoomLevel, isCompactMode, completionFilter }) => {
  const timeSlots = [];
  for (let hour = 0; hour < 24; hour++) {
    timeSlots.push(hour);
  }

  const getAssignmentsForHour = (hour) => {
    return (assignments || []).filter(a => {
      if (!a.due_date) return false;
      try {
        const assignmentDate = parseISO(a.due_date);
        const assignmentHour = assignmentDate.getHours();
        const assignmentDay = startOfDay(assignmentDate);
        const currentDay = startOfDay(currentDate);
        if (completionFilter === 'completed' && !isCompletedStatus(a.status)) return false;
        if (completionFilter === 'incomplete' && isCompletedStatus(a.status)) return false;
        return isSameDay(assignmentDay, currentDay) && assignmentHour === hour;
      } catch (error) {
        return false;
      }
    });
  };

  const getCourseInfo = (courseId) => {
    if (courseId && typeof courseId === 'object' && courseId._id) {
      return courseId;
    }
    if (courseId && typeof courseId === 'string') {
      return (courses || []).find(course => course._id === courseId);
    }
    return null;
  };

  const dayAssignments = (assignments || []).filter(a => {
    if (!a.due_date) return false;
    try {
      const assignmentDate = startOfDay(parseISO(a.due_date));
      const currentDay = startOfDay(currentDate);
      if (completionFilter === 'completed' && !isCompletedStatus(a.status)) return false;
      if (completionFilter === 'incomplete' && isCompletedStatus(a.status)) return false;
      return isSameDay(assignmentDate, currentDay);
    } catch (error) {
      return false;
    }
  });

  return (
    <DayViewContainer $zoomLevel={zoomLevel} $isCompactMode={isCompactMode}>
      <DayHeaderContainer $isCompactMode={isCompactMode}>
        <DayTitle>{format(currentDate, 'EEEE, MMMM d, yyyy')}</DayTitle>
        <DayStats>
          {dayAssignments.length} assignment{dayAssignments.length !== 1 ? 's' : ''} today
        </DayStats>
      </DayHeaderContainer>
      <DayContentContainer $zoomLevel={zoomLevel} $isCompactMode={isCompactMode}>
        <TimeColumn>
          {timeSlots.map(hour => (
            <TimeSlot key={hour} $isCompactMode={isCompactMode}>
              {(() => { const tz = settings?.preferences?.timezone || 'UTC'; const timeFormat = settings?.preferences?.timeFormat || '12h'; const d = new Date(); d.setHours(hour,0,0,0); return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: timeFormat === '12h', timeZone: tz }); })()}
            </TimeSlot>
          ))}
        </TimeColumn>
        <DayColumn>
          {timeSlots.map(hour => {
            const hourAssignments = getAssignmentsForHour(hour);
            return (
              <DayHourSlot key={hour} $isCompactMode={isCompactMode}>
                {hourAssignments.map((assignment, index) => {
                  const course = getCourseInfo(assignment.course);
                  let topPosition = 5;
                  try {
                    const assignmentDate = parseISO(assignment.due_date);
                    const minutes = assignmentDate.getMinutes();
                    topPosition = Math.floor((minutes / 60) * 50) + 5;
                  } catch (error) {
                    topPosition = index * 25 + 5;
                  }
                  return (
                    <DayAssignmentItem
                      key={assignment._id}
                      color={assignment.color || course?.color || '#5865f2'}
                      style={{ top: `${topPosition}px`, cursor: 'pointer', position: 'relative', paddingRight: '2rem' }}
                      onMouseEnter={(e) => onAssignmentHover(assignment, e)}
                      onMouseLeave={onAssignmentLeave}
                      onClick={() => onAssignmentClick(assignment)}
                    >
                      <div style={{ fontWeight: 'bold', fontSize: '0.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {assignment.course && (assignment.course.course_code ? `${assignment.course.course_code}: ` : `${assignment.course.name}: `)}{assignment.title}
                      </div>
                      {assignment.status === 'completed' && (
                        <div style={{ fontSize: '0.7rem', opacity: 0.8 }}>Completed</div>
                      )}
                      <DayAssignmentNavigateIcon
                        onClick={(e) => {
                          e.stopPropagation();
                          onAssignmentNavigate(assignment);
                        }}
                        onMouseEnter={(e) => {
                          e.stopPropagation();
                          onAssignmentLeave();
                        }}
                        title="Go to assignment details"
                        aria-label="Go to assignment details page"
                      >
                        <FaStickyNote />
                      </DayAssignmentNavigateIcon>
                    </DayAssignmentItem>
                  );
                })}
              </DayHourSlot>
            );
          })}
        </DayColumn>
      </DayContentContainer>
    </DayViewContainer>
  );
};

const AgendaView = ({ assignments, courses, settings, onAssignmentHover, onAssignmentLeave, onAssignmentClick, onAssignmentNavigate, completionFilter, isCompactMode }) => {
  const getCourseInfo = (courseId) => {
    if (courseId && typeof courseId === 'object' && courseId._id) {
      return courseId;
    }
    if (courseId && typeof courseId === 'string') {
      return (courses || []).find(course => course._id === courseId);
    }
    return null;
  };

  const filteredAssignments = assignments
    .filter(a => {
      if (!a.due_date) return false;
      if (completionFilter === 'completed' && a.status !== 'completed') return false;
      if (completionFilter === 'incomplete' && a.status === 'completed') return false;
      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.due_date);
      const dateB = new Date(b.due_date);
      return dateB - dateA;
    });

  const groupedAssignments = filteredAssignments.reduce((groups, assignment) => {
    const date = format(parseISO(assignment.due_date), 'yyyy-MM-dd');
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(assignment);
    return groups;
  }, {});

  return (
    <AgendaContainer $isCompactMode={isCompactMode}>
      <AgendaHeader $isCompactMode={isCompactMode}>
        <AgendaTitle $isCompactMode={isCompactMode}>Agenda View</AgendaTitle>
        <AgendaStats $isCompactMode={isCompactMode}>{filteredAssignments.length} assignments</AgendaStats>
      </AgendaHeader>
      <AgendaContent>
        {Object.keys(groupedAssignments).length === 0 ? (
          <EmptyState>No assignments found</EmptyState>
        ) : (
          Object.keys(groupedAssignments).sort((a, b) => b.localeCompare(a)).map(dateStr => {
            const dayAssignments = (groupedAssignments[dateStr] || []).slice().sort((a, b) => new Date(b.due_date) - new Date(a.due_date));
            const date = parseISO(dateStr);
            const isTodayDate = isToday(date);
            const isPast = date < new Date() && !isTodayDate;

            return (
              <AgendaDay key={dateStr}>
                <AgendaDayHeader $isToday={isTodayDate} $isPast={isPast} $isCompactMode={isCompactMode}>
                  <AgendaDayDate $isCompactMode={isCompactMode}>{format(date, 'EEEE, MMMM d, yyyy')}</AgendaDayDate>
                  <AgendaDayCount $isCompactMode={isCompactMode}>{dayAssignments.length} assignment{dayAssignments.length !== 1 ? 's' : ''}</AgendaDayCount>
                </AgendaDayHeader>
                <AgendaAssignments $isCompactMode={isCompactMode}>
                  {dayAssignments.map(assignment => {
                    const course = getCourseInfo(assignment.course);
                    return (
                      <AgendaItem
                        key={assignment._id}
                        $isCompleted={assignment.status === 'completed'}
                        onMouseEnter={(e) => onAssignmentHover(assignment, e)}
                        onMouseLeave={onAssignmentLeave}
                        onClick={() => onAssignmentClick(assignment)}
                        style={{ cursor: 'pointer', position: 'relative', paddingRight: '3rem' }}
                      >
                        <AgendaItemTime>
                          {(() => {
                              const tz = settings?.preferences?.timezone || 'UTC';
                              const timeFormat = settings?.preferences?.timeFormat || '12h';
                              try {
                                const d = new Date(assignment.due_date);
                                return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: timeFormat === '12h', timeZone: tz });
                              } catch (e) {
                                return format(parseISO(assignment.due_date), 'HH:mm');
                              }
                            })()}
                        </AgendaItemTime>
                        <AgendaItemContent>
                          <AgendaItemTitle style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {course ? `${course.course_code}: ` : ''}{assignment.title}
                          </AgendaItemTitle>
                          {assignment.description && (
                            <AgendaItemDescription>{assignment.description}</AgendaItemDescription>
                          )}
                          <AgendaItemMeta>
                            <span>Due: {format(parseISO(assignment.due_date), 'MMM d, yyyy')}</span>
                            {assignment.status === 'completed' && <span>Completed</span>}
                          </AgendaItemMeta>
                        </AgendaItemContent>
                        <AgendaItemColor color={assignment.color || course?.color || '#5865f2'} />
                        <AgendaItemNavigateIcon
                          onClick={(e) => {
                            e.stopPropagation();
                            onAssignmentNavigate(assignment);
                          }}
                          onMouseEnter={(e) => {
                            e.stopPropagation();
                            onAssignmentLeave();
                          }}
                          title="Go to assignment details"
                        >
                          <FaStickyNote />
                        </AgendaItemNavigateIcon>
                      </AgendaItem>
                    );
                  })}
                </AgendaAssignments>
              </AgendaDay>
            );
          })
        )}
      </AgendaContent>
    </AgendaContainer>
  );
};

const WeekView = ({ currentDate, assignments, courses, settings, onAssignmentHover, onAssignmentLeave, onAssignmentClick, onAssignmentNavigate, zoomLevel, isCompactMode, completionFilter }) => {
  const weekStartsOn = settings.preferences?.weekStart === 'monday' ? 1 : 0;
  const weekStart = startOfWeekFn(currentDate, { weekStartsOn });
  const weekEnd = endOfWeekFn(currentDate, { weekStartsOn });
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

  const timeSlots = [];
  for (let hour = 0; hour < 24; hour++) {
    timeSlots.push(hour);
  }

  const getAssignmentsForDay = (day) => {
    return (assignments || []).filter(a => {
      if (!a.due_date) return false;
      try {
        const assignmentDate = startOfDayFn(parseISO(a.due_date));
        const currentDay = startOfDay(day);
        if (completionFilter === 'completed' && a.status !== 'completed') return false;
        if (completionFilter === 'incomplete' && a.status === 'completed') return false;
        return isSameDay(assignmentDate, currentDay);
      } catch (error) {
        return false;
      }
    });
  };

  const getCourseInfo = (courseId) => {
    if (courseId && typeof courseId === 'object' && courseId._id) {
      return courseId;
    }
    if (courseId && typeof courseId === 'string') {
      return (courses || []).find(course => course._id === courseId);
    }
    return null;
  };

  return (
    <WeekViewContainer $zoomLevel={zoomLevel} $isCompactMode={isCompactMode}>
      <WeekContentContainer>
        <WeekHeadersContainer>
          {weekDays.map(day => (
            <WeekDayColumn key={`header-${day}`}>
              <WeekDayHeader>
                <div style={{ fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                  {format(day, 'EEE')}
                </div>
                <div style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>
                  {format(day, 'd')}
                </div>
              </WeekDayHeader>
            </WeekDayColumn>
          ))}
        </WeekHeadersContainer>
        <WeekContentGrid $zoomLevel={zoomLevel} $isCompactMode={isCompactMode}>
          <TimeColumn>
            {timeSlots.map(hour => (
              <TimeSlot key={hour} $isCompactMode={isCompactMode}>
                {(() => { const tz = settings?.preferences?.timezone || 'UTC'; const timeFormat = settings?.preferences?.timeFormat || '12h'; const d = new Date(); d.setHours(hour,0,0,0); return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: timeFormat === '12h', timeZone: tz }); })()}
              </TimeSlot>
            ))}
          </TimeColumn>
          {weekDays.map(day => {
            const dayAssignments = getAssignmentsForDay(day);
            const isTodayDate = isToday(day);
            return (
              <WeekDayColumn key={day}>
                {timeSlots.map(hour => (
                  <WeekDaySlot key={hour} $isToday={isTodayDate} $isCompactMode={isCompactMode}>
                    {dayAssignments
                      .filter((assignment) => {
                        try {
                          const assignmentDate = parseISO(assignment.due_date);
                          const assignmentHour = assignmentDate.getHours();
                          return assignmentHour === hour;
                        } catch (error) {
                          return false;
                        }
                      })
                      .map((assignment, index) => {
                        const course = getCourseInfo(assignment.course);
                        return (
                          <WeekAssignmentItem
                            key={assignment._id}
                            color={assignment.color || course?.color || '#5865f2'}
                            style={{ top: `${index * 25 + 5}px`, cursor: 'pointer', position: 'relative', paddingRight: '1.5rem' }}
                            onMouseEnter={(e) => onAssignmentHover(assignment, e)}
                            onMouseLeave={onAssignmentLeave}
                            onClick={() => onAssignmentClick(assignment)}
                          >
                            {course ? `${course.course_code}: ` : ''}{assignment.title}
                            <WeekAssignmentNavigateIcon
                              onClick={(e) => {
                                e.stopPropagation();
                                onAssignmentNavigate(assignment);
                              }}
                              onMouseEnter={(e) => {
                                e.stopPropagation();
                                onAssignmentLeave();
                              }}
                              title="Go to assignment details"
                              aria-label="Go to assignment details page"
                            >
                              <FaStickyNote />
                            </WeekAssignmentNavigateIcon>
                          </WeekAssignmentItem>
                        );
                      })}
                  </WeekDaySlot>
                ))}
              </WeekDayColumn>
            );
          })}
        </WeekContentGrid>
      </WeekContentContainer>
    </WeekViewContainer>
  );
};

const DayViewContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 200px);
  transform: scale(${props => props.$zoomLevel});
  transform-origin: top left;
  width: ${props => `${100 / props.$zoomLevel}%`};
  height: ${props => `${100 / props.$zoomLevel}%`};
`;

const DayHeaderContainer = styled.div`
  padding: ${props => props.$isCompactMode ? '1rem' : '2rem'};
  background: ${props => props.theme.cardBackground};
  border-bottom: 2px solid #5865f2;
`;

const DayTitle = styled.h2`
  color: ${props => props.theme.text};
  margin: 0 0 0.5rem 0;
  font-size: 1.8rem;
  font-weight: 600;
`;

const DayStats = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 1rem;
`;

const DayContentContainer = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr;
  gap: 1px;
  background: ${props => props.theme.buttonPrimary};
  height: calc(100vh - 200px);
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: ${props => props.theme.border} ${props => props.theme.background};

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.background};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.border};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.textMuted};
  }
`;

const DayColumn = styled.div`
  background: ${props => props.theme.background};
  display: flex;
  flex-direction: column;
`;

const DayHourSlot = styled.div`
  height: ${props => props.$isCompactMode ? '40px' : '60px'};
  border-bottom: 1px solid ${props => props.theme.border};
  position: relative;
  padding: 0.25rem;
`;

const DayAssignmentItem = styled.div`
  position: absolute;
  left: 2px;
  right: 2px;
  background: ${props => props.color || '#5865f2'};
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  z-index: 10;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DayAssignmentNavigateIcon = styled.button`
  position: absolute;
  right: 0.25rem;
  top: 50%;
  transform: translateY(-50%);
  background: ${props => props.theme.buttonPrimary};
  border: 2px solid ${props => props.theme.buttonPrimary};
  border-radius: 4px;
  color: white;
  cursor: pointer;
  padding: 0.25rem;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.9;
  transition: all 0.2s ease;
  min-width: 24px;
  min-height: 24px;

  &:hover {
    opacity: 1;
    background: ${props => props.theme.buttonPrimaryHover};
    border-color: ${props => props.theme.buttonPrimaryHover};
    transform: translateY(-50%) scale(1.05);
  }

  &:focus {
    outline: 2px solid ${props => props.theme.buttonPrimary};
    outline-offset: 2px;
    opacity: 1;
  }

  @media (max-width: 768px) {
    font-size: 0.6rem;
    padding: 0.2rem;
    min-width: 20px;
    min-height: 20px;
  }
`;

const AgendaContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
  @media (max-width: 480px) {
    min-height: 0;
  }
`;

const AgendaHeader = styled.div`
  margin-bottom: 2rem;
  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
  @media (max-width: 480px) {
    margin-bottom: 0.5rem;
  }
`;

const AgendaTitle = styled.h2`
  color: ${props => props.theme.text};
  margin: 0 0 0.5rem 0;
  font-size: ${props => props.$isCompactMode ? '1.5rem' : '2rem'};
  font-weight: 600;
`;

const AgendaStats = styled.div`
  color: ${props => props.theme.textMuted};
  font-size: ${props => props.$isCompactMode ? '0.9rem' : '1.1rem'};
`;

const AgendaContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-bottom: 2rem;
  @media (max-width: 480px) {
    padding-bottom: 3rem;
  }
  scrollbar-width: thin;
  scrollbar-color: ${props => props.theme.border} ${props => props.theme.background};

  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: ${props => props.theme.background};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.border};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.textMuted};
  }
  @media (max-width: 768px) {
    -webkit-overflow-scrolling: touch;
  }
`;

const AgendaDay = styled.div`
  margin-bottom: ${props => props.$isCompactMode ? '1rem' : '2rem'};
  @media (max-width: 480px) {
    margin-bottom: 0.5rem;
  }
`;

const AgendaDayHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.$isCompactMode ? '0.5rem' : '1rem'};
  background: ${props => 
    props.$isToday ? 'rgba(88, 101, 242, 0.2)' : 
    props.$isPast ? 'rgba(240, 71, 71, 0.1)' : 
    props.theme.cardBackground
  };
  border-radius: 8px;
  margin-bottom: ${props => props.$isCompactMode ? '0.5rem' : '1rem'};
  border: ${props => props.$isToday ? '2px solid #5865f2' : 'none'};
`;

const AgendaDayDate = styled.div`
  font-size: ${props => props.$isCompactMode ? '1rem' : '1.2rem'};
  font-weight: 600;
  color: ${props => props.theme.text};
`;

const AgendaDayCount = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: ${props => props.$isCompactMode ? '0.8rem' : '0.9rem'};
`;

const AgendaAssignments = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.$isCompactMode ? '0.25rem' : '0.5rem'};
`;

const AgendaItem = styled.div`
  display: flex;
  align-items: center;
  padding: ${props => props.$isCompactMode ? '0.5rem' : '1rem'};
  background: ${props => props.theme.cardBackground};
  border-radius: 8px;
  border: 1px solid ${props => props.$isCompleted ? '#57f287' : '#5865f2'};
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: ${props => props.$isCompleted ? 0.7 : 1};

  &:hover {
    box-shadow: 0 2px 8px ${props => props.$isCompleted ? 'rgba(87, 242, 135, 0.3)' : 'rgba(88, 101, 242, 0.3)'};
  }
`;

const AgendaItemTime = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => props.theme.text};
  min-width: 80px;
  margin-right: 1rem;
`;

const AgendaItemContent = styled.div`
  flex: 1;
`;

const AgendaItemTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin-bottom: 0.25rem;
`;

const AgendaItemDescription = styled.div`
  font-size: 0.85rem;
  color: ${props => props.theme.textSecondary};
  margin-bottom: 0.25rem;
`;

const AgendaItemMeta = styled.div`
  font-size: 0.8rem;
  color: ${props => props.theme.textSecondary};
  display: flex;
  gap: 1rem;
`;

const AgendaItemColor = styled.div`
  width: 12px;
  height: 12px;
  background: ${props => props.color || '#5865f2'};
  border-radius: 50%;
  margin-left: 1rem;
`;

const AgendaItemNavigateIcon = styled.button`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.1);
  border: none;
  border-radius: 4px;
  color: ${props => props.theme.text};
  cursor: pointer;
  padding: 0.25rem;
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  transition: all 0.2s ease;

  &:hover {
    opacity: 1;
    background: rgba(0, 0, 0, 0.2);
    transform: translateY(-50%) scale(1.1);
  }

  @media (max-width: 768px) {
    font-size: 0.6rem;
    padding: 0.2rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  color: ${props => props.theme.textSecondary};
  font-size: 1.2rem;
  padding: 3rem;
`;

const UpcomingDeadlinesSidebar = ({ assignments, courses, settings, onAssignmentHover, onAssignmentLeave, onClose }) => {
  const getCourseInfo = (courseId) => {
    if (courseId && typeof courseId === 'object' && courseId._id) {
      return courseId;
    }
    if (courseId && typeof courseId === 'string') {
      return (courses || []).find(course => course._id === courseId);
    }
    return null;
  };

  const upcomingAssignments = assignments
    .filter(a => {
      if (!a.due_date || a.status === 'completed') return false;
      try {
        const dueDate = startOfDay(parseISO(a.due_date));
        const today = startOfDay(new Date());
        const sevenDaysFromNow = addDays(today, 7);
        return dueDate >= today && dueDate <= sevenDaysFromNow;
      } catch (error) {
        return false;
      }
    })
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));

  const groupedAssignments = upcomingAssignments.reduce((groups, assignment) => {
    const dateKey = format(parseISO(assignment.due_date), 'yyyy-MM-dd');
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(assignment);
    return groups;
  }, {});

  return (
    <SidebarContainer>
      <SidebarHeader>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <div>
            <SidebarTitle>Upcoming Deadlines</SidebarTitle>
            <SidebarCount>{upcomingAssignments.length} assignments</SidebarCount>
          </div>
          <CloseButton onClick={onClose}>×</CloseButton>
        </div>
      </SidebarHeader>
      
      <SidebarContent>
        {Object.keys(groupedAssignments).length === 0 ? (
          <EmptyState>No upcoming deadlines in the next 7 days!</EmptyState>
        ) : (
          Object.entries(groupedAssignments)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([dateKey, dayAssignments]) => {
              const date = parseISO(dateKey);
              const isTodayDate = isToday(date);
              const isTomorrowDate = isTomorrow(date);
              
              return (
                <SidebarDay key={dateKey}>
                  <SidebarDayHeader>
                    <SidebarDayTitle>
                      {isTodayDate ? 'Today' : isTomorrowDate ? 'Tomorrow' : format(date, 'EEEE, MMM d')}
                    </SidebarDayTitle>
                    <SidebarDayCount>{dayAssignments.length}</SidebarDayCount>
                  </SidebarDayHeader>
                  
                  <SidebarAssignments>
                    {dayAssignments.map(assignment => {
                      const course = getCourseInfo(assignment.course);
                      const formatTimeForUser = (iso) => {
                        if (!iso) return null;
                        try {
                          const tz = settings?.preferences?.timezone || 'UTC';
                          const timeFormat = settings?.preferences?.timeFormat || '12h';
                          const d = new Date(iso);
                          return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: timeFormat === '12h', timeZone: tz });
                        } catch (e) {
                          return null;
                        }
                      };

                      const dueTime = assignment.due_date && assignment.due_date.includes('T') ? formatTimeForUser(assignment.due_date) : null;
                      return (
                        <SidebarAssignmentItem
                          key={assignment._id}
                          onMouseEnter={(e) => onAssignmentHover(assignment, e)}
                          onMouseLeave={() => onAssignmentLeave()}
                          aria-label={`${getCourseInfo(assignment.course)?.course_code ? `${getCourseInfo(assignment.course).course_code}: ` : ''}${assignment.title}`}
                        >
                          <SidebarAssignmentColor color={assignment.color || course?.color || '#5865f2'} />
                          <SidebarAssignmentContent>
                            <SidebarAssignmentTitle>
                              {course ? `${course.course_code}: ` : ''}{assignment.title}
                            </SidebarAssignmentTitle>
                            {dueTime && (
                              <SidebarAssignmentTime>Due at {dueTime}</SidebarAssignmentTime>
                            )}
                          </SidebarAssignmentContent>
                        </SidebarAssignmentItem>
                      );
                    })}
                  </SidebarAssignments>
                </SidebarDay>
              );
            })
        )}
      </SidebarContent>
    </SidebarContainer>
  );
};

const SidebarContainer = styled.div`
  width: 300px;
  background: ${props => props.theme.background};
  border-left: 2px solid #5865f2;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 120px);
  position: fixed;
  right: 0;
  top: 120px;
  z-index: 10;
  box-shadow: -4px 0 12px rgba(0, 0, 0, 0.3);
`;

const SidebarHeader = styled.div`
  padding: 1.5rem;
  background: linear-gradient(135deg, #5865f2 0%, #4752c4 100%);
  border-bottom: 1px solid ${props => props.theme.border};
`;

const SidebarTitle = styled.h3`
  color: ${props => props.theme.text};
  margin: 0 0 0.5rem 0;
  font-size: 1.2rem;
  font-weight: 600;
`;

const SidebarCount = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 0.9rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.text};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: background-color 0.2s;
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const SidebarContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
`;

const SidebarDay = styled.div`
  margin-bottom: 1.5rem;
`;

const SidebarDayHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  padding: 0.75rem;
  background: rgba(88, 101, 242, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(88, 101, 242, 0.2);
`;

const SidebarDayTitle = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${props => props.theme.text};
`;

const SidebarDayCount = styled.div`
  color: ${props => props.theme.text};
  font-size: 0.8rem;
  background: #5865f2;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-weight: 600;
`;

const SidebarAssignments = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SidebarAssignmentItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem;
  background: ${props => props.theme.cardBackground};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid rgba(255, 255, 255, 0.05);

  &:hover {
    transform: translateX(2px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    border-color: rgba(88, 101, 242, 0.3);
  }
`;

const SidebarAssignmentColor = styled.div`
  width: 8px;
  height: 8px;
  background: ${props => props.color || '#5865f2'};
  border-radius: 50%;
  margin-right: 0.75rem;
  flex-shrink: 0;
`;

const SidebarAssignmentContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const SidebarAssignmentTitle = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${props => props.theme.text};
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SidebarAssignmentTime = styled.div`
  font-size: 0.75rem;
  color: ${props => props.theme.textSecondary};
`;

const Tooltip = styled.div`
  position: fixed;
  background: linear-gradient(135deg, #5865f2 0%, #4752c4 100%);
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
  z-index: 1000;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transform: translate(-50%, -100%);
  margin-top: -10px;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #4752c4;
  }
`;

const CalendarView = () => {
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [currentView, setCurrentView] = useState(VIEW_TYPES.MONTH);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [hoveredAssignment, setHoveredAssignment] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isCompactMode, setIsCompactMode] = useState(true);
  const [completionFilter, setCompletionFilter] = useState('all');
  const [showSidebar, setShowSidebar] = useState(false);
  const { assignments, courses, settings, parseGrade, formatGrade } = useData();

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const weekStartsOn = settings?.preferences?.weekStart === 'monday' ? 1 : 0;
  const calendarStart = startOfWeek(monthStart, { weekStartsOn });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn });
  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const dayElementsRef = useRef({});
  const [connectingLine, setConnectingLine] = useState(null);

  useEffect(() => {
    if (!settings || !settings.display) return;
    if (typeof settings.display.showUpcomingDeadlines !== 'undefined') {
      setShowSidebar(Boolean(settings.display.showUpcomingDeadlines));
    } else {
      setShowSidebar(true);
    }
  }, [settings]);

  const [upgradePrompt, setUpgradePrompt] = useState({ feature: null, visible: false });
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [editModalAssignment, setEditModalAssignment] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const tooltipTimeoutRef = useRef({ timeoutId: null, updatePosition: null });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDatePicker && !event.target.closest('.date-picker-container')) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDatePicker]);

  const prevPeriod = useCallback(() => {
    if (currentView === VIEW_TYPES.MONTH) {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
    } else if (currentView === VIEW_TYPES.DAY) {
      setCurrentMonth(subDays(currentMonth, 1));
    } else {
      setCurrentMonth(subWeeks(currentMonth, 1));
    }
  }, [currentView, currentMonth]);

  const nextPeriod = useCallback(() => {
    if (currentView === VIEW_TYPES.MONTH) {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
    } else if (currentView === VIEW_TYPES.DAY) {
      setCurrentMonth(addDays(currentMonth, 1));
    } else {
      setCurrentMonth(addWeeks(currentMonth, 1));
    }
  }, [currentView, currentMonth]);

  const handleTodayClick = () => {
    setCurrentMonth(new Date());
  };

  // Calculations engine computing the drawing data layer coordinates
  useEffect(() => {
    if (currentView !== VIEW_TYPES.MONTH || !days.length || !assignments) {
      setConnectingLine(null);
      return;
    }

    const todayStr = format(startOfDay(new Date()), 'yyyy-MM-dd');
    const todayEl = dayElementsRef.current[todayStr];

    if (!todayEl) {
      setConnectingLine(null);
      return;
    }

    const parsedAssignments = (assignments || [])
      .filter(a => a.due_date && a.status !== 'completed' && completionFilter !== 'completed')
      .map(a => ({ ...a, parsedDate: startOfDay(parseISO(a.due_date)) }))
      .filter(a => a.parsedDate >= startOfDay(new Date()))
      .sort((a, b) => a.parsedDate - b.parsedDate);

    const nextAssignment = parsedAssignments[0];

    if (!nextAssignment) {
      setConnectingLine(null);
      return;
    }

    const assignmentDateStr = format(nextAssignment.parsedDate, 'yyyy-MM-dd');
    const targetEl = dayElementsRef.current[assignmentDateStr];

    if (!targetEl || todayStr === assignmentDateStr) {
      setConnectingLine(null);
      return;
    }

    const updateLineCoordinates = () => {
      const containerRect = document.getElementById('calendar-grid-container')?.getBoundingClientRect();
      if (!containerRect || !todayEl || !targetEl) return;

      const todayRect = todayEl.getBoundingClientRect();
      const targetRect = targetEl.getBoundingClientRect();

      const x1 = todayRect.left + todayRect.width / 2 - containerRect.left;
      const y1 = todayRect.top + todayRect.height / 2 - containerRect.top;
      const x2 = targetRect.left + targetRect.width / 2 - containerRect.left;
      const y2 = targetRect.top + targetRect.height / 2 - containerRect.top;

      const diffTime = Math.abs(nextAssignment.parsedDate - startOfDay(new Date()));
      const daysTill = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      const labelText = `${daysTill} day${daysTill !== 1 ? 's' : ''} till due`;

      // Functional state update prevents recursive cycles
      setConnectingLine(prev => {
        if (prev && prev.x1 === x1 && prev.y1 === y1 && prev.x2 === x2 && prev.y2 === y2 && prev.labelText === labelText) {
          return prev; 
        }
        return { x1, y1, x2, y2, labelText };
      });
    };

    // Use a small frame timeout to let the DOM settle before measuring elements
    const timeoutId = setTimeout(updateLineCoordinates, 50);
    window.addEventListener('resize', updateLineCoordinates);
    
    const gridContainer = todayEl.closest('.days-grid-scroll-container');
    if (gridContainer) gridContainer.addEventListener('scroll', updateLineCoordinates);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateLineCoordinates);
      if (gridContainer) gridContainer.removeEventListener('scroll', updateLineCoordinates);
    };
  }, [currentView, currentMonth, assignments, completionFilter, days.length]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' || event.target.tagName === 'SELECT') {
        return;
      }
      switch (event.key) {
        case 'ArrowLeft':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            prevPeriod();
          }
          break;
        case 'ArrowRight':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            nextPeriod();
          }
          break;
        case 't':
        case 'T':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            handleTodayClick();
          }
          break;
        case 'm':
        case 'M':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            setCurrentView(VIEW_TYPES.MONTH);
          }
          break;
        case 'w':
        case 'W':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            setCurrentView(VIEW_TYPES.WEEK);
          }
          break;
        case 'd':
        case 'D':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            setCurrentView(VIEW_TYPES.DAY);
          }
          break;
        case 'a':
        case 'A':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            setCurrentView(VIEW_TYPES.AGENDA);
          }
          break;
        case '+':
        case '=':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            setZoomLevel(prev => Math.min(prev + 0.1, 2));
          }
          break;
        case '-':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            setZoomLevel(prev => Math.max(prev - 0.1, 0.5));
          }
          break;
        case '0':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            setZoomLevel(1);
          }
          break;
        case 'c':
        case 'C':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            setIsCompactMode(prev => !prev);
          }
          break;
        case 's':
        case 'S':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            setShowSidebar(prev => !prev);
          }
          break;
        default:
          break;
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentView, nextPeriod, prevPeriod]);

  const handleAssignmentHover = (assignment, event) => {
    const course = getCourseInfo(assignment.course);
    if (tooltipTimeoutRef.current.timeoutId) {
      clearTimeout(tooltipTimeoutRef.current.timeoutId);
      if (tooltipTimeoutRef.current.updatePosition) {
        document.removeEventListener('mousemove', tooltipTimeoutRef.current.updatePosition);
      }
    }
    const updateTooltipPosition = (e) => {
      setTooltipPosition({ x: e.clientX, y: e.clientY });
    };
    document.addEventListener('mousemove', updateTooltipPosition);
    tooltipTimeoutRef.current.updatePosition = updateTooltipPosition;
    setTooltipPosition({ x: event.clientX, y: event.clientY });
    tooltipTimeoutRef.current.timeoutId = setTimeout(() => {
      setHoveredAssignment({ assignment, course });
    }, 500);
  };

  const handleAssignmentLeave = () => {
    if (tooltipTimeoutRef.current.timeoutId) {
      if (tooltipTimeoutRef.current.updatePosition) {
        document.removeEventListener('mousemove', tooltipTimeoutRef.current.updatePosition);
      }
      clearTimeout(tooltipTimeoutRef.current.timeoutId);
      tooltipTimeoutRef.current.timeoutId = null;
      tooltipTimeoutRef.current.updatePosition = null;
    }
    setHoveredAssignment(null);
  };

  const handleAssignmentClick = (assignment) => {
    setEditModalAssignment(assignment);
    setIsEditModalOpen(true);
  };

  const handleAssignmentNavigate = (assignment) => {
    const courseId = assignment.course?._id || assignment.course;
    if (courseId && assignment._id) {
      navigate(`/assignments/${courseId}/${assignment._id}`);
    }
  };

  const handleMouseMove = (event) => {
    if (hoveredAssignment) {
      let x = event.clientX;
      let y = event.clientY;
      const tooltipWidth = 320; 
      const tooltipHeight = 70; 
      if (x - tooltipWidth / 2 < 10) {
        x = tooltipWidth / 2 + 10;
      } else if (x + tooltipWidth / 2 > window.innerWidth - 10) {
        x = window.innerWidth - tooltipWidth / 2 - 10;
      }
      if (y - tooltipHeight < 10) {
        y = tooltipHeight + 20; 
      } else if (y + tooltipHeight > window.innerHeight - 10) {
        y = window.innerHeight - tooltipHeight - 10; 
      }
      setTooltipPosition({ x, y });
    }
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayHeaders = [];
  const weekStart = startOfWeek(new Date(), { weekStartsOn });
  for (let i = 0; i < 7; i++) {
    const day = new Date(weekStart);
    day.setDate(weekStart.getDate() + i);
    dayHeaders.push({ name: format(day, 'EEE'), key: `header-${i}` });
  }

  const handleSidebarToggle = () => {
    setShowSidebar(!showSidebar);
  };

  const handleViewChange = (newView) => {
    setCurrentView(newView);
    setCurrentMonth(new Date());
  };

  const handleDateDisplayClick = () => {
    setShowDatePicker(!showDatePicker);
  };

  const handleMonthSelect = (monthIndex) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), monthIndex, 1));
    setShowDatePicker(false);
  };

  const handleYearChange = (yearChange) => {
    const newDate = new Date(currentMonth);
    newDate.setFullYear(currentMonth.getFullYear() + yearChange);
    setCurrentMonth(newDate);
  };

  const getCourseInfo = (courseId) => {
    if (courseId && typeof courseId === 'object' && courseId._id) {
      return courseId;
    }
    if (courseId && typeof courseId === 'string') {
      return (courses || []).find(course => course._id === courseId);
    }
    return null;
  };

  return (
    <CalendarContainer onMouseMove={handleMouseMove}>
      <Header>
        {currentView !== VIEW_TYPES.AGENDA && (
          <NavigationControls>
            <TodayButton onClick={handleTodayClick}>Today</TodayButton>
            <ArrowButton onClick={prevPeriod}>‹</ArrowButton>
            <ArrowButton onClick={nextPeriod}>›</ArrowButton>
          </NavigationControls>
        )}
        
        {currentView !== VIEW_TYPES.AGENDA && (
          <DateDisplay className="date-picker-container" onClick={handleDateDisplayClick}>
            {currentView === VIEW_TYPES.MONTH 
              ? format(currentMonth, 'MMMM yyyy')
              : currentView === VIEW_TYPES.DAY
              ? format(currentMonth, 'EEEE, MMMM d, yyyy')
              : currentView === VIEW_TYPES.AGENDA
              ? `Agenda - ${format(currentMonth, 'MMMM yyyy')}`
              : `Week of ${format(startOfWeekFn(currentMonth, { weekStartsOn: settings?.preferences?.weekStart === 'monday' ? 1 : 0 }), 'MMM d')} - ${format(endOfWeekFn(currentMonth, { weekStartsOn: settings?.preferences?.weekStart === 'monday' ? 1 : 0 }), 'MMM d, yyyy')}`
            }
          </DateDisplay>
        )}

        {showDatePicker && currentView !== VIEW_TYPES.AGENDA && (
          <DatePickerDropdown className="date-picker-container">
            <YearControls>
              <ArrowButton onClick={() => handleYearChange(-1)}>‹</ArrowButton>
              <YearDisplay>{currentMonth.getFullYear()}</YearDisplay>
              <ArrowButton onClick={() => handleYearChange(1)}>›</ArrowButton>
            </YearControls>
            <MonthGrid>
              {months.map((month, index) => (
                <MonthButton
                  key={month}
                  selected={index === currentMonth.getMonth()}
                  onClick={() => handleMonthSelect(index)}
                >
                  {month.slice(0, 3)}
                </MonthButton>
              ))}
            </MonthGrid>
          </DatePickerDropdown>
        )}

        <ViewDropdown value={currentView} onChange={(e) => handleViewChange(e.target.value)}>
          <option value={VIEW_TYPES.MONTH}>Month</option>
          <option value={VIEW_TYPES.WEEK}>Week</option>
          <option value={VIEW_TYPES.DAY}>Day</option>
          <option value={VIEW_TYPES.AGENDA}>Agenda</option>
        </ViewDropdown>

        <AdditionalControls>
          {currentView !== VIEW_TYPES.AGENDA && (
            <>
              {(currentView === VIEW_TYPES.WEEK || currentView === VIEW_TYPES.DAY) && (
                <ZoomControls>
                  <ControlButton onClick={() => setZoomLevel(Math.max(0.5, zoomLevel - 0.1))} disabled={zoomLevel <= 0.5}>
                    <ZoomIcon>-</ZoomIcon>
                  </ControlButton>
                  <ZoomLevel>{Math.round(zoomLevel * 100)}%</ZoomLevel>
                  <ControlButton onClick={() => setZoomLevel(Math.min(2, zoomLevel + 0.1))} disabled={zoomLevel >= 2}>
                    <ZoomIcon>+</ZoomIcon>
                  </ControlButton>
                </ZoomControls>
              )}

              <CompactToggle>
                <CompactButton $active={isCompactMode} onClick={() => setIsCompactMode(!isCompactMode)}>
                  Compact
                </CompactButton>
              </CompactToggle>

              <FilterDropdown value={completionFilter} onChange={(e) => setCompletionFilter(e.target.value)}>
                <option value="all">All Assignments</option>
                <option value="completed">Completed</option>
                <option value="incomplete">Incomplete</option>
              </FilterDropdown>

              <SidebarToggle>
                <SidebarButton $active={showSidebar} onClick={handleSidebarToggle}>
                  Deadlines
                </SidebarButton>
              </SidebarToggle>
            </>
          )}
        </AdditionalControls>
      </Header>
      <CalendarGridContainer id="calendar-grid-container" style={{ position: 'relative' }}>
        {currentView === VIEW_TYPES.MONTH ? (
          <>
            <DaysGridContainer className="days-grid-scroll-container">
              {connectingLine && (
                <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 15 }}>
                  <defs>
                    <marker id="arrow" viewBox="0 0 10 10" refX="6" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
                      <path d="M 0 1 L 10 5 L 0 9 z" fill="#5865f2" />
                    </marker>
                  </defs>
                  <line 
                    x1={connectingLine.x1} 
                    y1={connectingLine.y1} 
                    x2={connectingLine.x2} 
                    y2={connectingLine.y2} 
                    stroke="#5865f2" 
                    strokeWidth="3" 
                    strokeDasharray="6,4"
                    markerEnd="url(#arrow)"
                  />
                  <rect
                    x={(connectingLine.x1 + connectingLine.x2) / 2 - 50}
                    y={(connectingLine.y1 + connectingLine.y2) / 2 - 12}
                    width="100"
                    height="22"
                    rx="4"
                    fill="#4752c4"
                  />
                  <text
                    x={(connectingLine.x1 + connectingLine.x2) / 2}
                    y={(connectingLine.y1 + connectingLine.y2) / 2 + 4}
                    fill="white"
                    fontSize="11"
                    fontWeight="bold"
                    textAnchor="middle"
                  >
                    {connectingLine.labelText}
                  </text>
                </svg>
              )}

              <DayHeadersContainer>
                {dayHeaders.map(header => (
                  <DayHeader key={header.key}>{header.name}</DayHeader>
                ))}
              </DayHeadersContainer>
              <DaysGrid>
                {days.map(day => {
                  const dayStr = format(day, 'yyyy-MM-dd');
                  const dayAssignments = (assignments || []).filter(a => {
                    if (!a.due_date) return false;
                    try {
                      const assignmentDate = startOfDay(parseISO(a.due_date));
                      const isSameDayAssignment = isSameDay(assignmentDate, day);
                      if (completionFilter === 'completed' && a.status !== 'completed') return false;
                      if (completionFilter === 'incomplete' && a.status === 'completed') return false;
                      return isSameDayAssignment;
                    } catch (error) {
                      return false;
                    }
                  });
                  const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
                  const isTodayDate = isToday(day);
                  return (
                    <Day 
                      key={day.toString()} 
                      $isCurrentMonth={isCurrentMonth} 
                      $isToday={isTodayDate}
                      ref={el => { dayElementsRef.current[dayStr] = el; }}
                    >
                      <div style={{ fontWeight: '600', color: 'var(--color-text)', fontSize: '0.75rem', marginBottom: '0.25rem' }}>{format(day, 'd')}</div>
                      {dayAssignments.map(a => {
                        const course = getCourseInfo(a.course);
                        return (
                          <AssignmentTitle 
                            key={a._id} 
                            color={a.color || course?.color || '#5865f2'}
                            onMouseEnter={(e) => handleAssignmentHover(a, e)}
                            onMouseLeave={handleAssignmentLeave}
                            onClick={() => handleAssignmentClick(a)}
                            style={{ cursor: 'pointer', position: 'relative', paddingRight: '2rem' }}
                          >
                            {course ? (course.course_code ? `${course.course_code}: ` : `${course.name}: `) : ''}{a.title}
                            <AssignmentNavigateIcon
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAssignmentNavigate(a);
                              }}
                              onMouseEnter={(e) => {
                                e.stopPropagation();
                                handleAssignmentLeave();
                              }}
                              title="Go to assignment details"
                              aria-label="Go to assignment details page"
                            >
                              <FaStickyNote />
                            </AssignmentNavigateIcon>
                          </AssignmentTitle>
                        );
                      })}
                      {dayAssignments.length > 0 && (
                        <AssignmentIndicator color={dayAssignments[0]?.color || (dayAssignments[0]?.course ? getCourseInfo(dayAssignments[0].course)?.color : '#5865f2')} />
                      )}
                    </Day>
                  );
                })}
              </DaysGrid>
            </DaysGridContainer>
          </>
        ) : currentView === VIEW_TYPES.WEEK ? (
          <WeekView 
            currentDate={currentMonth}
            assignments={assignments}
            courses={courses}
            settings={settings}
            onAssignmentHover={handleAssignmentHover}
            onAssignmentLeave={handleAssignmentLeave}
            onAssignmentClick={handleAssignmentClick}
            onAssignmentNavigate={handleAssignmentNavigate}
            zoomLevel={zoomLevel}
            isCompactMode={isCompactMode}
            completionFilter={completionFilter}
          />
        ) : currentView === VIEW_TYPES.DAY ? (
          <DayView 
            currentDate={currentMonth}
            assignments={assignments}
            courses={courses}
            settings={settings}
            onAssignmentHover={handleAssignmentHover}
            onAssignmentLeave={handleAssignmentLeave}
            onAssignmentClick={handleAssignmentClick}
            onAssignmentNavigate={handleAssignmentNavigate}
            zoomLevel={zoomLevel}
            isCompactMode={isCompactMode}
            completionFilter={completionFilter}
          />
        ) : currentView === VIEW_TYPES.AGENDA ? (
          <AgendaView 
            currentDate={currentMonth}
            assignments={assignments}
            courses={courses}
            settings={settings}
            onAssignmentHover={handleAssignmentHover}
            onAssignmentLeave={handleAssignmentLeave}
            onAssignmentClick={handleAssignmentClick}
            onAssignmentNavigate={handleAssignmentNavigate}
            zoomLevel={zoomLevel}
            isCompactMode={isCompactMode}
            completionFilter={completionFilter}
          />
        ) : null}
      </CalendarGridContainer>

      {/* Tooltip */}
      {hoveredAssignment && (
        <Tooltip
          style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y}px`
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
            {hoveredAssignment.course ? `${hoveredAssignment.course.course_code}: ${hoveredAssignment.course.name || hoveredAssignment.course.course_code}` : 'No Course Assigned'}
          </div>
          <div style={{ marginBottom: '0.25rem', fontWeight: '600' }}>{hoveredAssignment.assignment.title}</div>
          {hoveredAssignment.assignment.description && (
            <div style={{ marginBottom: '0.25rem', color: '#e6e6e6' }}>{hoveredAssignment.assignment.description}</div>
          )}
          <div>
            Due: {(() => {
              try {
                const d = new Date(hoveredAssignment.assignment.due_date);
                const tz = settings?.preferences?.timezone || 'UTC';
                const timeFormat = settings?.preferences?.timeFormat || '12h';
                const dateStr = d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', timeZone: tz });
                const timeStr = hoveredAssignment.assignment.due_date && hoveredAssignment.assignment.due_date.includes('T') ? d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: timeFormat === '12h', timeZone: tz }) : '';
                return `${dateStr}${timeStr ? ` at ${timeStr}` : ''}`;
              } catch (e) {
                return format(new Date(hoveredAssignment.assignment.due_date), 'PPP');
              }
            })()}
            {hoveredAssignment.assignment.status === 'completed' && (() => {
              const grade = parseGrade(hoveredAssignment.assignment.grade);
              const letterGrade = grade !== null ? formatGrade(grade) : 'Ungraded';
              return ` - Completed: ${letterGrade}`;
            })()}
          </div>
        </Tooltip>
      )}
      
      {showSidebar && (
        <UpcomingDeadlinesSidebar 
          assignments={assignments}
          courses={courses}
          settings={settings}
          onAssignmentHover={handleAssignmentHover}
          onAssignmentLeave={handleAssignmentLeave}
          onClose={() => setShowSidebar(false)}
        />
      )}

      {upgradePrompt.visible && (
        <CalendarUpgradePrompt
          feature={upgradePrompt.feature}
          onUpgrade={() => {
            setUpgradePrompt({ feature: null, visible: false });
            setIsSubscriptionModalOpen(true);
          }}
          onClose={() => setUpgradePrompt({ feature: null, visible: false })}
        />
      )}

      <SubscriptionModal
        isOpen={isSubscriptionModalOpen}
        onClose={() => setIsSubscriptionModalOpen(false)}
      />

      <CalendarAssignmentEditModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditModalAssignment(null);
        }}
        assignment={editModalAssignment}
        onSubmit={() => {}}
      />
    </CalendarContainer>
  );
};

export default CalendarView;
